<template>
  <div class="app-pagination__wrapper">
    <div v-if="length > 1" class="app-pagination d-flex justify-center align-center flex-wrap">
      <v-btn icon small :disabled="value === 1" @click="go(value - 1)">
        <v-icon small>mdi-chevron-left</v-icon>
      </v-btn>

      <template v-for="(item, i) in pages">
        <span v-if="item === '...'" :key="'ellipsis-' + i" class="app-pagination__ellipsis"> ... </span>

        <button
          v-else
          :key="'page-' + item"
          class="app-pagination__btn"
          :class="{ 'app-pagination__btn--active': item === value }"
          @click="go(item)"
        >
          {{ item }}
        </button>
      </template>

      <v-btn icon small :disabled="value === length" @click="go(value + 1)">
        <v-icon small>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Sempre visível: seletor de itens por página + contador -->
    <div class="app-pagination__right d-flex align-center">
      <v-select
        v-if="itemsPerPageOptions.length"
        :value="itemsPerPage"
        :items="itemsPerPageOptions"
        outlined
        dense
        hide-details
        class="app-pagination__per-page mr-2"
        @change="$emit('update:itemsPerPage', $event)"
      />
      <span v-if="total" class="app-pagination__counter">
        {{ (value - 1) * itemsPerPage + 1 }}-{{ Math.min(value * itemsPerPage, total) }} de {{ total }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppPagination',

  props: {
    value: { type: Number, default: 1 },
    length: { type: Number, default: 1 },
    itemsPerPage: { type: Number, default: 10 },
    itemsPerPageOptions: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
  },

  computed: {
    pages() {
      const current = this.value
      const total = this.length

      if (total <= 10) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }

      const EDGE = 4
      const DELTA = 2

      const nearLeft = current - DELTA
      const nearRight = current + DELTA

      const isStartPure = nearRight <= EDGE
      const isEndPure = nearLeft >= total - EDGE + 1
      const isNearStart = !isStartPure && nearLeft < EDGE
      const isNearEnd = !isEndPure && nearRight > total - EDGE + 1

      const nums = new Set()

      if (isStartPure || isEndPure) {
        for (let i = 1; i <= EDGE; i++) nums.add(i)
        for (let i = total - EDGE + 1; i <= total; i++) nums.add(i)
      } else if (isNearStart) {
        const end = Math.max(nearRight, EDGE + 3)
        for (let i = 1; i <= end; i++) nums.add(i)
        nums.add(total)
      } else if (isNearEnd) {
        nums.add(1)
        const start = Math.min(nearLeft, total - EDGE - 2)
        for (let i = start; i <= total; i++) nums.add(i)
      } else {
        nums.add(1)
        for (let i = nearLeft; i <= nearRight; i++) nums.add(i)
        nums.add(total)
      }

      const sorted = [...nums].sort((a, b) => a - b)
      const result = []
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...')
        result.push(sorted[i])
      }
      return result
    },
  },

  methods: {
    go(page) {
      if (page < 1 || page > this.length || page === this.value) return
      this.$emit('input', page)
    },
  },
}
</script>

<style scoped>
.app-pagination__wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 40px;
}

.app-pagination {
  gap: 4px;
}

.app-pagination__right {
  position: absolute;
  right: 0;
  gap: 4px;
}

.app-pagination__per-page {
  margin-top: 0;
  padding-top: 0;
  max-width: 90px;
  font-size: 13px;
}

.app-pagination__per-page ::v-deep .v-input__slot {
  min-height: 32px !important;
  border-radius: 10px !important;
}

.app-pagination__per-page ::v-deep fieldset {
  border-radius: inherit !important;
}

.app-pagination__counter {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
  white-space: nowrap;
}

.app-pagination__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-width: 32px;
  padding: 0 8px;
  border-radius: 16px;
  border: none;
  background-color: transparent;
  color: rgba(0, 0, 0, 0.87);
  font-size: 13px;
  font-weight: 400;
  font-family: inherit;
  cursor: pointer;
  outline: none;
  transition: background-color 0.2s;
  user-select: none;
}

.app-pagination__btn:hover:not(.app-pagination__btn--active) {
  background-color: rgba(0, 0, 0, 0.06);
}

.app-pagination__btn--active {
  background-color: var(--v-primary-base);
  color: #ffffff;
  font-weight: 500;
}

.app-pagination__ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.54);
  letter-spacing: 1px;
  user-select: none;
}
</style>
